import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, FileUp, Sparkles, BookOpen, ChevronDown, Scale, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

// One "Price Automatically" button that opens a small card with two ways to price:
//  • Price from Catalogue — matches against the vendor's saved catalogue (onCatalogue).
//  • Price from Local — uploads a price list (parsed in memory, never stored). Columns are
//    auto-matched by name and priced straight away; the adjust dialog only appears when a
//    header (description/rate) can't be matched. `getRfqId` resolves/creates the quotation.
export function PriceAutomaticallyButton({
  getRfqId, onCatalogue, onPriced, catEmpty = false,
  label = "Price Automatically", variant = "default",
  className = "h-11 gap-2 font-semibold", disabled = false,
  testid = "price-automatically-button", align = "end",
}) {
  const fileRef = useRef();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [rfqId, setRfqId] = useState(null);
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState([]);
  const [preview, setPreview] = useState([]);
  const [rows, setRows] = useState(0);
  const [sel, setSel] = useState({ description: "", unit: "", rate: "", code: "" });
  const [warnOpen, setWarnOpen] = useState(false);
  const [pending, setPending] = useState(null);

  // Always warn before auto-pricing: the BOQ quantities come from the contractor.
  const requestPrice = (action) => {
    if (action === "catalogue" && (catEmpty || !onCatalogue)) return;
    setMenuOpen(false);
    setPending(action);
    setWarnOpen(true);
  };

  const proceedPricing = async () => {
    const action = pending;
    setWarnOpen(false);
    setPending(null);
    if (action === "catalogue") {
      setBusy(true);
      try { await onCatalogue(); } finally { setBusy(false); }
    } else if (action === "local") {
      fileRef.current?.click();
    }
  };

  const goQuantityReview = async () => {
    setWarnOpen(false);
    setPending(null);
    setBusy(true);
    try {
      const rid = await getRfqId();
      if (rid) navigate(`/qreview?type=quantity&rfq=${rid}`);
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const chooseCatalogue = () => requestPrice("catalogue");

  const chooseLocal = () => requestPrice("local");

  const runPrice = async (rid, f, mapping) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("desc_col", mapping.description);
    fd.append("unit_col", mapping.unit || "");
    fd.append("rate_col", mapping.rate || "");
    fd.append("code_col", mapping.code || "");
    const { data } = await api.post(`/rfqs/${rid}/auto-price-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    const ms = data.match_summary || {};
    if (ms.ai_skipped) {
      toast.warning(`Matching skipped — your ${ms.plan_name} plan's ${ms.quota} automatic quotes are used up this month`);
    } else if (ms.matched === 0) {
      toast.warning(`Priced 0 of ${ms.total} lines — nothing in the price list matched. The file was not stored.`);
    } else {
      toast.success(`Priced ${ms.matched} of ${ms.total} line item(s) from your price list (${ms.list_items} rows) — the file was not stored`);
    }
    onPriced?.(data);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const rid = await getRfqId();
      if (!rid) return;
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/rfqs/${rid}/price-list/inspect`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.columns?.length) {
        toast.error("That file has no readable columns");
        return;
      }
      const ps = data.preselect || {};
      const mapping = {
        description: ps.description || "",
        unit: ps.unit || "",
        rate: ps.rate || "",
        code: ps.code || "",
      };
      // Column names matched cleanly — price straight away, no confirm step.
      if (mapping.description && mapping.rate) {
        await runPrice(rid, f, mapping);
        return;
      }
      // A header didn't line up — let the vendor adjust the mapping before pricing.
      setRfqId(rid);
      setFile(f);
      setCols(data.columns);
      setPreview(data.preview || []);
      setRows(data.rows || 0);
      setSel(mapping);
      setOpen(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not read that file");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!file || !rfqId) return;
    if (!sel.description) {
      toast.error("Pick which column holds the item description");
      return;
    }
    setBusy(true);
    try {
      await runPrice(rfqId, file, sel);
      setOpen(false);
      setFile(null);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not price from that file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} data-testid="price-file-input" />
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button variant={variant} data-testid={testid} disabled={disabled || busy} className={className}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {label}
            <ChevronDown className="w-4 h-4 opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-2 bg-card z-50 shadow-xl" align={align} data-testid="price-menu">
          <button
            data-testid="opt-catalogue"
            onClick={chooseCatalogue}
            disabled={catEmpty}
            className="w-full text-left rounded-lg p-3 flex items-start gap-3 hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="w-9 h-9 rounded-lg bg-brand-soft flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-brand" />
            </span>
            <span>
              <span className="block font-semibold text-sm text-slate-900 dark:text-slate-100">Price from Catalogue</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                {catEmpty ? "Your catalogue is empty — add items first." : "Match every line to your saved catalogue rates."}
              </span>
            </span>
          </button>
          <button
            data-testid="opt-local"
            onClick={chooseLocal}
            className="w-full text-left rounded-lg p-3 flex items-start gap-3 hover:bg-accent transition-colors mt-0.5"
          >
            <span className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
              <FileUp className="w-4 h-4 text-primary" />
            </span>
            <span>
              <span className="block font-semibold text-sm text-slate-900 dark:text-slate-100">Price from Local</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                Upload a price list from your drive — it's matched in memory and never stored.
              </span>
            </span>
          </button>
          <div className="mt-1.5 px-3 pt-2 pb-1 text-[11px] text-muted-foreground border-t border-border flex items-start gap-1.5">
            <BookOpen className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Keep your catalogue and price list up to date so every line matches accurately.</span>
          </div>
        </PopoverContent>
      </Popover>

      {warnOpen && (
        <Dialog open={warnOpen} onOpenChange={(o) => { if (!busy) { setWarnOpen(o); if (!o) setPending(null); } }}>
          <DialogContent className="bg-card max-w-lg" data-testid="price-qty-warning-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> Check the quantities first
              </DialogTitle>
              <DialogDescription>
                The quantities on this BOQ were set by the contractor. Automatic pricing multiplies your
                rates by those quantities as-is. Confirm they look right, or run a Quantity Review to
                compare them against your own measurement BOQ before you price.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-3 flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={goQuantityReview} disabled={busy}
                      data-testid="warn-run-qreview-button" className="gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />} Run Quantity Review
              </Button>
              <Button onClick={proceedPricing} disabled={busy}
                      data-testid="warn-confirm-button" className="gap-2">
                <Sparkles className="w-4 h-4" /> Confirm quantities & continue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {open && (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
          <DialogContent className="bg-card max-w-2xl" data-testid="price-map-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Adjust the price-list columns</DialogTitle>
              <DialogDescription>
                We couldn't match every column by name in your file ({rows} rows). Pick the right columns below — the file is never stored, and we'll remember this map next time.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 mt-2">
              {[
                ["description", "Description", true],
                ["rate", "Rate / price", false],
                ["unit", "Unit", false],
                ["code", "Item code", false],
              ].map(([key, lbl, req]) => (
                <div key={key}>
                  <Label>{lbl}{req && <span className="text-rose-500"> *</span>}</Label>
                  <Select value={sel[key] || "__none__"} onValueChange={(v) => setSel({ ...sel, [key]: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid={`map-${key}-select`} className="mt-1.5 bg-background"><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent className="bg-card max-h-64">
                      {!req && <SelectItem value="__none__">— none —</SelectItem>}
                      {cols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {preview.length > 0 && (
              <div className="mt-3 border border-border rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-secondary">
                    <tr>{cols.map((c) => <th key={c} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        {cols.map((c) => <td key={c} className="px-2 py-1.5 whitespace-nowrap max-w-[180px] truncate text-muted-foreground">{row[c]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="map-cancel-button">Cancel</Button>
              <Button onClick={confirm} disabled={busy || !sel.description} data-testid="map-confirm-button" className="gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Price now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
